import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export function ContactPage() {
  const [formData, setFormData] = useState({
    company: '',
    contact: '',
    email: '',
    phone: '',
    productInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    if (!formData.email || !formData.company || !formData.contact) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5r6s52hs78ip5370f70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          company: formData.company,
          contact: formData.contact,
          email: formData.email,
          phone: formData.phone,
          productInterest: formData.productInterest,
          message: formData.message.slice(0, 500) // Limit to 500 characters
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          contact: '',
          email: '',
          phone: '',
          productInterest: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Limit textarea to 500 characters
    if (name === 'message' && value.length > 500) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Telefon',
      info: '+49 172 9606061',
      link: 'tel:+491729606061'
    },
    {
      icon: 'ri-mail-line',
      title: 'E-Mail',
      info: 'info@horizonsignage.de',
      link: 'mailto:info@horizonsignage.de'
    },
    {
      icon: 'ri-time-line',
      title: 'Öffnungszeiten',
      info: 'Mo-Fr: 8:00-18:00 Uhr',
      link: null
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Standort',
      info: 'Speditionstrasse 15a, 40221 Düsseldorf',
      link: null
    }
  ];

  const trustBadges = [
    {
      icon: 'ri-customer-service-2-line',
      title: 'Kostenlose Beratung',
      description: 'Individuelle Beratung für Ihr Projekt'
    },
    {
      icon: 'ri-time-line',
      title: 'Schnelle Rückmeldung',
      description: 'Antwort innerhalb von 24 Stunden'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Individuelle Lösungen',
      description: 'Maßgeschneidert für Ihre Anforderungen'
    }
  ];

  const schema = {
    '@type': 'ContactPage',
    name: 'Kontakt - Unverbindliches Angebot anfordern',
    description: 'Kontaktieren Sie Horizon Signage für eine kostenlose Beratung zu Ihrem Projekt. Telefon: +49 172 9606061, E-Mail: info@horizonsignage.de',
    mainEntity: {
      '@type': 'Organization',
      name: 'Horizon Signage',
      telephone: '+49-172-9606061',
      email: 'info@horizonsignage.de',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Speditionstrasse 15a',
        addressLocality: 'Düsseldorf',
        postalCode: '40221',
        addressCountry: 'DE'
      }
    }
  };

  return (
    <>
      <SEO
        title="Kontakt - Unverbindliches Angebot anfordern | Horizon Signage Düsseldorf"
        description="Kontaktieren Sie Horizon Signage für eine kostenlose Beratung zu Ihrem Projekt. Telefon: +49 172 9606061, E-Mail: info@horizonsignage.de. Standort: Speditionstrasse 15a, 40221 Düsseldorf. Schnelle Rückmeldung innerhalb von 24 Stunden."
        keywords="Kontakt, Angebot, Beratung, Horizon Signage, Düsseldorf, Telefon, E-Mail"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/kontakt`}
        schema={schema}
      />
      <div className="min-h-screen">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
              <span className="inline-block px-4 py-2 bg-zinc-800 text-white rounded-full text-sm font-semibold uppercase tracking-wider mb-6">
                Kontakt
              </span>
              <h1 className="text-5xl lg:text-6xl font-bold text-zinc-800 leading-tight mb-6">
                Unverbindliches Angebot anfordern
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Wir beraten Sie gerne zu Ihrem Projekt und erstellen Ihnen ein 
                maßgeschneidertes Angebot für Ihre Promotion-Fahnen.
              </p>
            </div>
          </section>

          {/* Main Contact Section */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                {/* Contact Information */}
                <div>
                  <h2 className="text-3xl font-bold text-zinc-800 mb-8">
                    Sprechen Sie uns an
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    Unser erfahrenes Team steht Ihnen bei allen Fragen zu unseren 
                    Produkten und Dienstleistungen zur Verfügung.
                  </p>

                  {/* Contact Info Cards */}
                  <div className="space-y-6 mb-12">
                    {contactInfo.map((item, index) => (
                      <div key={index} className="flex items-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <div className="w-12 h-12 flex items-center justify-center bg-zinc-800 rounded-lg mr-4">
                          <i className={`${item.icon} text-white text-xl`}></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-zinc-800 mb-1">{item.title}</h3>
                          {item.link ? (
                            <a
                              href={item.link}
                              className="text-gray-600 hover:text-zinc-800 transition-colors"
                            >
                              {item.info}
                            </a>
                          ) : (
                            <p className="text-gray-600">{item.info}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Trust Badges */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-zinc-800 mb-6">Ihre Vorteile</h3>
                    {trustBadges.map((badge, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full mr-4 mt-1 flex-shrink-0">
                          <i className={`${badge.icon} text-green-600 text-sm`}></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-zinc-800 mb-1">{badge.title}</h4>
                          <p className="text-gray-600 text-sm">{badge.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Form */}
                <div>
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                    <h3 className="text-2xl font-bold text-zinc-800 mb-6">
                      Angebot anfordern
                    </h3>
                    
                    {submitStatus === 'success' && (
                      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center">
                          <i className="ri-check-circle-line text-green-600 mr-2"></i>
                          <span className="text-green-800">Vielen Dank! Ihre Anfrage wurde erfolgreich versendet.</span>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                          <i className="ri-error-warning-line text-red-600 mr-2"></i>
                          <span className="text-red-800">Es gab einen Fehler. Bitte versuchen Sie es erneut.</span>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} data-readdy-form>
                      {/* Company Name */}
                      <div className="mb-6">
                        <label htmlFor="company" className="block text-sm font-semibold text-zinc-800 mb-2">
                          Firmenname *
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-zinc-800 transition-colors"
                          placeholder="Ihr Firmenname"
                        />
                      </div>

                      {/* Contact Person */}
                      <div className="mb-6">
                        <label htmlFor="contact" className="block text-sm font-semibold text-zinc-800 mb-2">
                          Ansprechpartner *
                        </label>
                        <input
                          type="text"
                          id="contact"
                          name="contact"
                          value={formData.contact}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-zinc-800 transition-colors"
                          placeholder="Vor- und Nachname"
                        />
                      </div>

                      {/* Email and Phone */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                          <label htmlFor="email" className="block text-sm font-semibold text-zinc-800 mb-2">
                            E-Mail *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-zinc-800 transition-colors"
                            placeholder="ihre.email@firma.de"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-semibold text-zinc-800 mb-2">
                            Telefon
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-zinc-800 transition-colors"
                            placeholder="+49 123 456789"
                          />
                        </div>
                      </div>

                      {/* Product Interest */}
                      <div className="mb-6">
                        <label htmlFor="productInterest" className="block text-sm font-semibold text-zinc-800 mb-2">
                          Produktinteresse
                        </label>
                        <select
                          id="productInterest"
                          name="productInterest"
                          value={formData.productInterest}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-zinc-800 transition-colors"
                        >
                          <option value="">Bitte wählen...</option>
                          <option value="Beachflags">Beachflags / Segelfahnen</option>
                          <option value="Fahnenmast-Flaggen">Fahnenmast-Flaggen</option>
                          <option value="Tischflaggen">Tischflaggen</option>
                          <option value="Standflaggen">Standflaggen</option>
                          <option value="Wimpelketten">Wimpelketten</option>
                          <option value="Nationalflaggen">Nationalflaggen</option>
                          <option value="Individuelle Werbeflaggen">Individuelle Werbeflaggen</option>
                          <option value="Beratung">Allgemeine Beratung</option>
                        </select>
                      </div>

                      {/* Message */}
                      <div className="mb-6">
                        <label htmlFor="message" className="block text-sm font-semibold text-zinc-800 mb-2">
                          Nachricht
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          maxLength={500}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-zinc-800 transition-colors resize-none"
                          placeholder="Beschreiben Sie Ihr Projekt, gewünschte Größen, Stückzahlen oder spezielle Anforderungen..."
                        />
                        <div className="text-right text-sm text-gray-500 mt-1">
                          {formData.message.length}/500 Zeichen
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-zinc-800 text-white font-semibold py-4 rounded-lg hover:bg-zinc-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap"
                      >
                        {isSubmitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            Anfrage absenden
                            <i className="ri-arrow-right-line ml-2"></i>
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
              <h2 className="text-4xl font-bold text-zinc-800 mb-6">
                Bevorzugen Sie den direkten Kontakt?
              </h2>
              <p className="text-xl text-gray-600 mb-10">
                Rufen Sie uns an für eine sofortige Beratung zu Ihrem Projekt.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+4917296060061"
                  className="inline-flex items-center justify-center px-8 py-4 bg-zinc-800 text-white font-semibold rounded-lg hover:bg-zinc-900 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Jetzt anrufen
                </a>
                <a
                  href="mailto:info@horizonsignage.de"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-zinc-800 text-zinc-800 font-semibold rounded-lg hover:bg-zinc-800 hover:text-white transition-colors whitespace-nowrap"
                >
                  <i className="ri-mail-line mr-2"></i>
                  E-Mail schreiben
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
